// ============================================================
// build-raw-model-input.ts
//
// Builds an LLM input from caller-supplied system + messages + tools
// VERBATIM — the injection-free counterpart to Conversation.toLlmInput.
//
// This is the hard guarantee behind the durable harness API: a raw turn
// touches ZERO Cotera prompt code. No coco system message, no learned
// facts, no registry skills, no "Current date and time" tail, no tool-key
// merging, no receipt splicing, no tool-image splicing. The only shared
// machinery is the AI SDK's own `convertToModelMessages`/`jsonSchema`
// primitives and the provider adapter's `finalizeMessages` replay hygiene
// (strip OpenAI itemId, Gemini thought signatures) — none of which inject
// content.
// ============================================================

import { convertToModelMessages, jsonSchema, type ToolSet } from 'ai';
import type { JSONSchema7 } from 'json-schema';
import { Ty } from '@cotera/era';
import objectHash from 'object-hash';
import type { Message as MessageClass } from '../chat/message';
import { llmInputBuilder } from './input-builder';
import type { LlmCallSettings } from './llm-input-types';
import type { ModelInputBuildResult } from './model-input-build-result';
import { modelAdapterFor } from './model-input-adapter';

/**
 * A caller-defined tool the model may call. Rendered to the provider VERBATIM
 * and never executed server-side — the model emits tool-calls and the caller
 * executes them out-of-band (the raw Anthropic-style loop).
 *
 * `inputSchema` is typed as {@link Ty.Scalar} (the JSON value type) so the
 * whole turn input remains serializable into the ATC KV store for Restate
 * replay; it is a JSON Schema object at runtime.
 */
export type RawToolSchema = {
  name: string;
  description?: string;
  /** JSON Schema for the tool input (JSON value). */
  inputSchema: Ty.Scalar;
};

/**
 * Render caller tool schemas into a no-`execute` {@link ToolSet}. Because there
 * is no `execute`, {@link streamText} surfaces the model's tool-calls with a
 * `tool-calls` finish reason instead of running anything.
 */
export function rawToolSet(tools: readonly RawToolSchema[]): ToolSet {
  return Object.fromEntries(
    tools.map((tool) => [
      tool.name,
      {
        description: tool.description ?? tool.name,
        // Scalar is the JSON value type; at runtime this is a JSON Schema object.
        inputSchema: jsonSchema(tool.inputSchema as JSONSchema7),
      },
    ])
  ) as ToolSet;
}

/**
 * Assemble a {@link ModelInputBuildResult} from caller input with no Cotera
 * prompt injection. Reuses {@link LLMInputBuilder.build} purely for the
 * provider-neutral plumbing (call settings, provider options, message
 * finalization, cache key) via a minimal renderer that returns the caller's
 * messages and system unchanged.
 */
export async function buildRawModelInput(params: {
  modelId: string;
  system?: string;
  messages: readonly MessageClass[];
  tools?: readonly RawToolSchema[];
  callSettings?: LlmCallSettings;
}): Promise<ModelInputBuildResult> {
  const { modelId, system, messages, tools, callSettings } = params;

  let builder = llmInputBuilder()
    .withMessages(messages)
    .model(modelAdapterFor(modelId));

  if (callSettings !== undefined) {
    builder = builder.withCallSettings(callSettings);
  }
  if (tools !== undefined && tools.length > 0) {
    builder = builder.withTools(rawToolSet(tools));
  }

  return builder.build((b) => ({
    conversationMessages: async () => {
      const llmMessages = await Promise.all(
        b
          .getMessages()
          .map((message) =>
            message.toLlmAsync({ workspaceDomainLoader: undefined })
          )
      );
      return convertToModelMessages(llmMessages);
    },
    system: async () => system ?? null,
    messagesCacheKey: (msgs) =>
      objectHash([system ?? null, ...msgs.map((m) => m.stableCacheKey())]),
  }));
}
